import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { claimsApi } from "../services/claims";
import type { TalentClaimSummary } from "../types/claims";
import { claimTimeLeft } from "../utils/claimTimeLeft";
import { getCurrentAcceptingStatus, subscribeAcceptingStatus } from "../lib/acceptingStatus";
import { getLiveControlVisible, subscribeLiveControlVisible } from "../lib/liveControlVisibility";
import "./ClaimAlertBanner.css";
import { currentLanguage, t } from "../i18n";
import { useAccountCache } from "../hooks/useAccountCache";
import "./PaintSkeleton.css";

// 🚨 "Wasn't played?" — a red row at the top of the talent's Requests page
// until they answer (founder, 2026-10-09). It sits ABOVE the request cards,
// never inside one, so no card's height changes (LOCKED card contract).
//
// One compact row of FIXED height (founder pick B, 1.5.2): a long song name
// or a long language never makes it taller. One claim: the song in quotes on
// line 1 (cut with … — the claim page shows it whole), "Fan says not played ·
// time left" on line 2. Several claims fold into ONE row, "2 fans say not
// played", with the soonest deadline; Respond opens that soonest claim and the
// rest are answered from the next row that appears. On a narrow phone or in a
// long language Respond turns into a round red arrow and line 2 shortens, so
// the song keeps its room and the time left is never cut (see Fit below).
//
// Asked when the page opens and whenever the app comes back to the
// foreground (the tab bar's pattern). A failed read shows nothing: the push
// and the email carry the same alarm, and an older server has no route yet.

const ALERT = "M12 3.6 2.4 20.4h19.2L12 3.6ZM12 9.8v5M12 17.6h.01";

const CHEVRON = "M9.5 5.5 16 12l-6.5 6.5";

// Soonest deadline first (the server sends them that way; never rely on it).
const deadline = (c: TalentClaimSummary) => (c.deadlineAt ? Date.parse(c.deadlineAt) : Infinity);

// The song's column never gets less than this share of the row while the
// Respond pill can still give way to the round button (measured, 1.5.2).
const TITLE_SHARE = 0.55;

// The smallest phones (320pt class; the CSS tightens at the same width):
// the song gets every pixel the labelled pill would take.
const NARROW = "(max-width: 359px)";

// Line 2's words: 0 the full sentence, 1 the short form, 2 none (time only).
type Words = 0 | 1 | 2;
type Fit = { sig: string; round: boolean; words: Words };

const ClaimAlertBanner: React.FC = () => {
  const navigate = useNavigate();
  // The last answer paints at once on a later visit — no push-down on every
  // tab open; a claim that is genuinely new eases in. Respond reads the claim
  // fresh on its own page.
  const [claims, setClaims] = useAccountCache<TalentClaimSummary[]>("reqs-claims", []);
  const [seen] = useState(() => new Set(claims.map((c) => c.orderId)));
  const [, setMinute] = useState(0);
  const [resized, setResized] = useState(0);
  // The floating Live/Offline pill (LiveStatusFloat) owns the top-right
  // corner whenever it shows — opted in, or Offline, which a talent often is
  // the morning after a gig. The row then starts just below it so Respond is
  // never under the pill — the gap is the pill's own measured height
  // (--live-float-h, see LiveStatusFloat), and there is none at all when the
  // pill is hidden. Same two stores, same rule as the pill itself.
  const [accepting, setAccepting] = useState(getCurrentAcceptingStatus() !== false);
  const [liveControl, setLiveControl] = useState(getLiveControlVisible() === true);
  useEffect(() => subscribeAcceptingStatus(setAccepting), []);
  useEffect(() => subscribeLiveControlVisible(setLiveControl), []);

  const load = useCallback(async () => {
    try {
      const res = await claimsApi.listOpen();
      setClaims(Array.isArray(res?.claims) ? res.claims : []);
    } catch {
      /* quiet — see header */
    }
  }, [setClaims]);

  useEffect(() => {
    void load();
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    // The time left reads in minutes at its finest — redraw once a minute.
    const tick = window.setInterval(() => setMinute((n) => n + 1), 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(tick);
    };
  }, [load]);

  const now = Date.now();
  // Past its deadline the server refunds on its own — nothing left to answer.
  const live = claims.filter((c) => !c.deadlineAt || Date.parse(c.deadlineAt) > now);
  // Fit, measured after paint (before the eye sees it) — a rotation or a
  // resize measures again. Two things give way, in this order, so the song
  // keeps its room and the time left is never cut (320pt phones, long
  // languages — founder, 1.5.2):
  //   1. Respond: a labelled pill while the words column keeps at least
  //      TITLE_SHARE of the row AND line 2's full sentence fits beside it;
  //      otherwise — and always on the smallest phones (320pt class) — a
  //      round red arrow button (the word stays for screen readers).
  //   2. Line 2's words: the full sentence, then the short form ("Not
  //      played"), then none at all — the time left alone (the red box and
  //      the alert icon already say what it is).
  const rowRef = useRef<HTMLDivElement>(null);
  const txtRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const first = live.length ? [...live].sort((a, b) => deadline(a) - deadline(b))[0] : null;
  const left = first ? claimTimeLeft(first.deadlineAt, now) : null;
  const sig = `${live.length > 1}|${!!left}|${currentLanguage()}|${resized}`;
  const [fitState, setFit] = useState<Fit>({ sig: "", round: false, words: 0 });
  const fit = fitState.sig === sig ? fitState : { sig, round: false, words: 0 as Words };
  const { round, words } = fit;
  const firstId = first?.orderId;
  useLayoutEffect(() => {
    const row = rowRef.current;
    const txt = txtRef.current;
    if (!row || !txt) return;
    const label = labelRef.current;
    const cut = !!label && label.scrollWidth > label.clientWidth + 0.5;
    if (!round && (window.matchMedia?.(NARROW).matches || txt.clientWidth < TITLE_SHARE * row.clientWidth || cut)) {
      setFit({ sig, round: true, words });
      return;
    }
    if (cut && words < (left ? 2 : 1)) {
      setFit({ sig, round, words: (words + 1) as Words });
    }
  }, [sig, round, words, left, firstId]);
  useEffect(() => {
    const again = () => setResized((n) => n + 1);
    window.addEventListener("resize", again);
    return () => window.removeEventListener("resize", again);
  }, []);

  if (!first) return null;
  const many = live.length > 1;
  const open = () => navigate(`/claims/${encodeURIComponent(first.orderId)}`);

  return (
    <div className={`cab-stack${liveControl || !accepting ? " cab-stack--under-float" : ""}`}>
      {/* The whole row answers a tap (drunk-proof); Respond is the visible
          button and bubbles to the same handler. */}
      <div key={first.orderId} ref={rowRef} className={`cab${round ? " cab--round" : ""}${seen.has(first.orderId) ? "" : " psk-arrive"}`} role="alert" onClick={open}>
        <span className="cab-ic" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d={ALERT} />
          </svg>
          {many && <span className="cab-n">{live.length}</span>}
        </span>
        <div className="cab-txt" ref={txtRef}>
          <div className="cab-t">
            {many ? (
              <span className="cab-cut">{t("claimAlertBanner.fansSayNotPlayed", { count: live.length })}</span>
            ) : (
              <span className="cab-cut">{t("claimAlertBanner.songQuoted", { song: first.searchQuery })}</span>
            )}
          </div>
          <div className="cab-s">
            {/* With no room for words the full sentence stays for screen
                readers; the eye gets the time left alone. */}
            <span className={words === 2 ? "cab-sr" : "cab-cut"} ref={labelRef}>
              {many
                ? t(words === 1 ? "claimAlertBanner.endsIn" : "claimAlertBanner.firstRunsOutIn")
                : t(words === 1 ? "claimAlertBanner.notPlayed" : "claimAlertBanner.fanSaysNotPlayed")}
            </span>
            {/* Whitespace-only text is not drawn in a flex row but keeps the
                words apart for screen readers and copy. */}
            {left && " "}
            {left && (
              <span className="cab-left">
                {!many && words < 2 && <span className="cab-dot">·</span>}
                {!many && words < 2 && " "}
                <svg className="cab-clk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                <b>{left}</b>
              </span>
            )}
          </div>
        </div>
        {/* Round, the arrow says "go"; the word stays for screen readers. */}
        <button type="button" className={round ? "cab-btn cab-btn--round" : "cab-btn"}>
          <span className={round ? "cab-sr" : undefined}>{t("claimAlertBanner.respond")}</span>
          {round && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={CHEVRON} />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default ClaimAlertBanner;
